import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getReconciliationReport } from '../../../../redux/actions/SocietyPortal/InvoiceReportAction/InvoiceReportAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { exportReconciliationReportReq } from '../../../../axios/request/SocietyPortal/InvoiceReportReqs/InvoiceReportReqs';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { PaginationLimit } from '../../../../components/PaginationLimit';

const ReconciliationReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { reconData, reconInvoiceTypeCols, reconSummary, reconPagination, reconLoading } = useSelector(state => state.InvoiceReportReducer || {});
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers || {});

    const [filters, setFilters] = useState({
        wing_id: '', flat_id: '',
        as_on_date: localDateString(),
        only_difference: false,
        search: '',
    });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (filters.wing_id) dispatch(getFlatsDropdown({ wing_id: filters.wing_id }));
    }, [dispatch, filters.wing_id]);

    const fetchReport = useCallback(() => {
        const params = { society_id: societyId, page, limit };
        if (filters.wing_id) params.wing_id = filters.wing_id;
        if (filters.flat_id) params.flat_id = filters.flat_id;
        if (filters.as_on_date) params.as_on_date = filters.as_on_date;
        if (filters.only_difference) params.only_difference = 'true';
        if (filters.search) params.search = filters.search;
        dispatch(getReconciliationReport(params));
    }, [dispatch, societyId, filters, page, limit]);
    usePageRefresh(fetchReport);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'wing_id') updated.flat_id = '';
            return updated;
        });
        setPage(1);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = { society_id: societyId };
            if (filters.wing_id) params.wing_id = filters.wing_id;
            if (filters.flat_id) params.flat_id = filters.flat_id;
            if (filters.as_on_date) params.as_on_date = filters.as_on_date;
            if (filters.only_difference) params.only_difference = 'true';
            if (filters.search) params.search = filters.search;
            const response = await exportReconciliationReportReq(params);
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'reconciliation_report.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed:', e);
        } finally {
            setExporting(false);
        }
    };

    const handleReset = () => {
        setFilters({ wing_id: '', flat_id: '', as_on_date: localDateString(), only_difference: false, search: '' });
        setPage(1);
    };

    const flats = reconData || [];
    const invoiceTypeCols = reconInvoiceTypeCols || [];
    const summary = reconSummary || {};
    const pagination = reconPagination || {};
    const wings = wingsDropdown || [];
    const flatsList = Array.isArray(flatsDropdown) ? flatsDropdown : flatsDropdown?.data || [];

    const formatCurrency = (amount) => parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Build dynamic columns
    const fixedBefore = [
        { header: 'Sr No', key: 'index', align: 'center', width: '50px' },
        { header: 'Flat No', key: 'flat_no' },
        { header: 'Member Name', key: 'member_name' },
        { header: 'Member Opening', key: 'member_opening', align: 'right', isCurrency: true },
    ];

    const dynamicCols = invoiceTypeCols.map(it => ({
        header: it.invoice_title,
        key: `type_${it.invoice_type_id}`,
        invoiceTypeId: it.invoice_type_id,
        align: 'right',
        isCurrency: true,
    }));

    const fixedAfter = [
        { header: 'Bill Due', key: 'bill_due', align: 'right', isCurrency: true },
        { header: 'DN Due', key: 'debit_note_due', align: 'right', isCurrency: true },
        { header: 'Open CN', key: 'open_credit_note', align: 'right', isCurrency: true },
        { header: 'Unalloc. Receipt', key: 'unallocated_receipt', align: 'right', isCurrency: true },
        { header: 'Unalloc. Advance', key: 'unallocated_advance', align: 'right', isCurrency: true },
        { header: 'Portal Due', key: 'total_portal_due', align: 'right', isCurrency: true },
        { header: 'Acct Receivable', key: 'account_receivable', align: 'right', isCurrency: true },
        { header: 'Difference', key: 'difference', align: 'right', isCurrency: true },
    ];

    const allColumns = useMemo(() => [...fixedBefore, ...dynamicCols, ...fixedAfter], [invoiceTypeCols]);

    const getCellValue = (flat, col, idx) => {
        if (col.key === 'index') return (page - 1) * limit + idx + 1;
        if (col.invoiceTypeId) return formatCurrency(flat.invoice_type_bifurcation?.[col.invoiceTypeId] || 0);
        if (col.isCurrency) return formatCurrency(flat[col.key]);
        return flat[col.key] || '-';
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Reconciliation Report</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">Portal dues vs GL account receivable comparison</p>
                </div>
                <button onClick={handleExport} disabled={exporting || reconLoading}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50">
                    {exporting ? <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" /> : <Icon icon="mdi:file-excel-outline" className="tw-text-sm" />}
                    {exporting ? 'Exporting...' : 'Export Excel'}
                </button>
            </div>

            {/* Summary Cards */}
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-mb-4">
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Flats</div>
                    <div className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mt-1">{summary.total_flats || 0}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Portal Due</div>
                    <div className="tw-text-xl tw-font-bold tw-text-blue-700 tw-mt-1">{formatCurrency(summary.total_portal_due)}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Acct Receivable (GL)</div>
                    <div className="tw-text-xl tw-font-bold tw-text-emerald-700 tw-mt-1">{formatCurrency(summary.total_account_receivable)}</div>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
                    <div className="tw-text-[11px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Difference</div>
                    <div className={`tw-text-xl tw-font-bold tw-mt-1 ${(summary.total_difference || 0) !== 0 ? 'tw-text-red-600' : 'tw-text-emerald-700'}`}>
                        {formatCurrency(summary.total_difference)}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Wing</label>
                        <select value={filters.wing_id} onChange={(e) => handleFilterChange('wing_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500">
                            <option value="">All Wings</option>
                            {wings.map(w => <option key={w.wing_id} value={w.wing_id}>{w.title}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Flat</label>
                        <select value={filters.flat_id} onChange={(e) => handleFilterChange('flat_id', e.target.value)} disabled={!filters.wing_id}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400">
                            <option value="">All Flats</option>
                            {flatsList.map(f => <option key={f.flat_id} value={f.flat_id}>{f.flat_number || f.flat_name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">As On Date</label>
                        <input type="date" value={filters.as_on_date} onChange={(e) => handleFilterChange('as_on_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search</label>
                        <div className="tw-relative">
                            <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input type="text" placeholder="Flat or member..." value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500" />
                        </div>
                    </div>
                    <div className="tw-flex tw-items-end tw-gap-3">
                        <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none tw-pb-1">
                            <input
                                type="checkbox"
                                checked={filters.only_difference}
                                onChange={(e) => handleFilterChange('only_difference', e.target.checked)}
                                className="form-check-input"
                            />
                            <span className="tw-text-xs tw-text-gray-600">Only mismatches</span>
                        </label>
                    </div>
                </div>
                <div className="tw-flex tw-justify-end tw-mt-3">
                    <button onClick={handleReset} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">
                        <Icon icon="mdi:refresh" className="tw-text-sm" /> Reset
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                {allColumns.map(col => (
                                    <th key={col.key} className="tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap"
                                        style={{ textAlign: col.align || 'left', minWidth: col.width || 'auto' }}>
                                        {col.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {reconLoading ? (
                                <tr><td colSpan={allColumns.length} className="tw-text-center tw-py-12">
                                    <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Loading report...</p>
                                </td></tr>
                            ) : flats.length === 0 ? (
                                <tr><td colSpan={allColumns.length} className="tw-text-center tw-py-12">
                                    <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No data found</p>
                                </td></tr>
                            ) : (
                                <>
                                    {flats.map((flat, idx) => {
                                        const hasDiff = (flat.difference || 0) !== 0;
                                        return (
                                            <tr key={flat.flat_id} className={`tw-border-b tw-border-gray-100 ${hasDiff ? 'tw-bg-red-50' : 'hover:tw-bg-gray-50/50'}`}>
                                                {allColumns.map(col => (
                                                    <td key={col.key}
                                                        className={`tw-px-3 tw-py-2 tw-whitespace-nowrap ${col.key === 'difference' && hasDiff ? 'tw-text-red-700 tw-font-bold' : col.key === 'total_portal_due' || col.key === 'account_receivable' ? 'tw-font-medium tw-text-gray-800' : 'tw-text-gray-700'}`}
                                                        style={{ textAlign: col.align || 'left' }}>
                                                        {getCellValue(flat, col, idx)}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                    {/* Summary Footer */}
                                    <tr className="tw-bg-gray-100 tw-border-t-2 tw-border-gray-300 tw-font-bold">
                                        {allColumns.map((col, i) => {
                                            if (col.key === 'index') return <td key={col.key} className="tw-px-3 tw-py-2.5"></td>;
                                            if (col.key === 'flat_no') return <td key={col.key} className="tw-px-3 tw-py-2.5 tw-text-gray-800">Totals</td>;
                                            if (col.key === 'member_name') return <td key={col.key} className="tw-px-3 tw-py-2.5"></td>;

                                            const summaryKeyMap = {
                                                member_opening: 'total_member_opening',
                                                bill_due: 'total_bill_due',
                                                debit_note_due: 'total_debit_note_due',
                                                open_credit_note: 'total_open_credit_note',
                                                unallocated_receipt: 'total_unallocated_receipt',
                                                unallocated_advance: 'total_unallocated_advance',
                                                total_portal_due: 'total_portal_due',
                                                account_receivable: 'total_account_receivable',
                                                difference: 'total_difference',
                                            };

                                            const sKey = summaryKeyMap[col.key];
                                            if (sKey) {
                                                const val = summary[sKey] || 0;
                                                return (
                                                    <td key={col.key} className={`tw-px-3 tw-py-2.5 tw-text-right tw-whitespace-nowrap ${col.key === 'difference' && val !== 0 ? 'tw-text-red-700' : 'tw-text-gray-800'}`}>
                                                        {formatCurrency(val)}
                                                    </td>
                                                );
                                            }

                                            // Dynamic invoice type columns - no summary available
                                            return <td key={col.key} className="tw-px-3 tw-py-2.5 tw-text-right tw-text-gray-400">-</td>;
                                        })}
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {pagination.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pagination.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}
        </>
    );
};

export default ReconciliationReport;
